import { Component, computed, HostListener, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AdminNavbarComponent } from './admin-cockpit/admin-navbar/admin-navbar.component';
import { CustomerNavbarComponent } from './cybersecurity-cockpit/customer-navbar/customer-navbar.component';
import { PartnerNavbarComponent } from './partners-cockpit/partner-navbar/partner-navbar.component';
import { CockpitService, PentestService } from '../../core/services';
import { AuthStore } from '../../core/stores';
import { Subject, takeUntil } from 'rxjs';
import { environment as frontend } from '../../../environment/environment';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import * as Paths from '../../core/constants';
import { Role } from '../../core/enums';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AdminSidebarComponent } from './admin-cockpit/admin-sidebar/admin-sidebar.component';
import { CustomerSidebarComponent } from './cybersecurity-cockpit/customer-sidebar/customer-sidebar.component';
import { PartnerSidebarComponent } from './partners-cockpit/partner-sidebar/partner-sidebar.component';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'dbx-main',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    AdminNavbarComponent,
    CustomerNavbarComponent,
    PartnerNavbarComponent,
    MatSidenavContainer,
    MatSidenav,
    MatSidenavContent,
    RouterLink,
    MatIcon,
    MatButtonModule,
    TranslatePipe,
    MatMenuTrigger,
    MatMenu,
    MatButton,
    MatMenuItem,
    AdminSidebarComponent,
    CustomerSidebarComponent,
    PartnerSidebarComponent,
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
})
export class MainComponent {
  private readonly authStore = inject(AuthStore);
  private readonly pentestService = inject(PentestService);
  private readonly cockpitService = inject(CockpitService);
  private readonly destroy$ = new Subject<void>();
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);

  isMobile = signal(false);
  isCollapsed = signal(false);

  protected readonly currentUser = this.authStore.user;

  protected readonly isUser = computed(() => this.currentUser()?.role === Role.User);
  protected readonly isGlobalAdmin = computed(() => this.currentUser()?.role === Role.GlobalAdmin);
  protected readonly isPartner = computed(() => this.currentUser()?.role === Role.Partner);
  protected readonly showCockpitSwitch = computed(() => this.isGlobalAdmin() || this.isPartner());

  protected readonly isPartnerCockpit = this.cockpitService.isPartner;
  protected readonly isAdminCockpit = this.cockpitService.isAdmin;
  protected readonly isCustomerCockpit = this.cockpitService.isCustomer;
  protected readonly canNavigateToCustomerCockpit = computed(() => !this.isCustomerCockpit());
  protected readonly canNavigateToPartnerCockpit = computed(() => !this.isUser() && !this.isPartnerCockpit());
  protected readonly canNavigateToAdminCockpit = computed(() => this.isGlobalAdmin() && !this.isAdminCockpit());

  app_versions = signal<string>('');

  mode = computed(() => (this.isMobile() ? 'over' : 'side'));

  readonly Paths = Paths;

  constructor() {
    this.pentestService
      .getAppVersion()
      .pipe(takeUntil(this.destroy$))
      .subscribe((backend) => {
        this.app_versions.set(`${backend.version} - ${frontend.version}`);
      });

    this.breakpointObserver.observe('(max-width: 1279px)').subscribe((result) => {
      this.isMobile.set(result.matches);

      if (result.matches) {
        this.isCollapsed.set(true);
      } else {
        this.isCollapsed.set(false);
      }
    });
  }

  toggleSidenav() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.isMobile()) return;

    const target = event.target as HTMLElement;

    if (!target.closest('.sidenav') && !target.closest('.chevron-btn')) {
      this.isCollapsed.set(true);
    }
  }

  isUrlActive(path: string[], exact = false): boolean {
    return this.router.isActive(this.router.createUrlTree(path), {
      paths: exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }
}
